import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { TWebsiteBuilderElement } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { TWebsiteBuilderContainer } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderContainer";
import { IWebsiteBuilderEditorHistoryActionBase, IWebsiteBuilderHistorySnapshot, WebsiteBuilderEditorActionType } from "../../websiteBuilderEditorAction";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { TWebsiteBuilderSectionEntity } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";
import { isWebsiteBuilderFreeformParentNode, TWebsiteBuilderFreeformParentNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderFreeformParentNode";
import { IWebsiteBuilderGeneralTraversalNode } from "../../actionHelpers/generalTraversal";

export interface IBringToBackElementActionPayload {
  id: string;
  parentId: string;
  originalZIndex: number;
  inverse: boolean;
}

export interface IBringToBackElementAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.BRING_TO_BACK_ELEMENT;
  payload: IBringToBackElementActionPayload;
}

export function getBringToBackElementInverseAction(action: IBringToBackElementAction): IBringToBackElementAction {
  const { id, parentId, originalZIndex } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.BRING_TO_BACK_ELEMENT,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: action.scrollNodeId,
    payload: {
      id,
      parentId,
      originalZIndex,
      inverse: true,
    },
  };
}

function toggleBringToBackElement<T extends TWebsiteBuilderFreeformParentNode>(
  id: string,
  parentNode: T,
  originalZIndex: number,
  inverse: boolean,
) {
  return {
    ...parentNode,
    children: parentNode.children.map((child: TWebsiteBuilderSectionEntity): TWebsiteBuilderSectionEntity => {
      let zIndex: number = null;

      if (child.id === id) {
        zIndex = inverse ? originalZIndex : 0;
      } else {
        zIndex = (child.type === "element" ? child.element.zIndex : child.container.zIndex) ?? 0;
        zIndex += inverse ? -1 : 1;
      }

      if (child.type === "element") {
        const updatedElement: TWebsiteBuilderElement = {
          ...child,
        };

        updatedElement.element = {
          ...updatedElement.element,
          zIndex,
        };

        return updatedElement;
      } else {
        const updatedContainer: TWebsiteBuilderContainer = {
          ...child,
        };

        updatedContainer.container = {
          ...updatedContainer.container,
          zIndex,
        };

        return updatedContainer;
      }
    }),
  };
}

export function applyBringToBackElementChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: IBringToBackElementAction,
): IWebsiteBuilderHistorySnapshot {
  const { id, parentId, originalZIndex, inverse } = action.payload;

  if (originalZIndex == null || originalZIndex === 0) {
    return { header, root, footer };
  }

  function traverse(node: IWebsiteBuilderGeneralTraversalNode): IWebsiteBuilderGeneralTraversalNode {
    if (node.id === parentId && isWebsiteBuilderFreeformParentNode(node)) {
      return toggleBringToBackElement(id, node, originalZIndex, inverse);
    } else if (!node.children) {
      return node;
    } else {
      return {
        ...node,
        children: node.children.map(traverse),
      };
    }
  }

  return {
    header: traverse(header) as IWebsiteBuilderHeader,
    root: traverse(root) as IWebsiteBuilderRoot,
    footer: traverse(footer) as IWebsiteBuilderFooter,
  };
}
